import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Plus, Pencil, Trash2, Flame, NotebookText } from 'lucide-react';
import { API } from '@/lib/api';
import {
  AlimentoSelector, DIAS, DIA_LABEL, TIEMPOS, SMAE_GRUPOS,
  emptyDias, emptyEquivalentes, normPlanDias,
} from './PlanAlimenticio';

const EMPTY_FORM = {
  nombre: '',
  tipo_dieta: '',
  modo: 'menu',
  calorias_objetivo: '',
  proteinas_g: '',
  carbohidratos_g: '',
  grasas_g: '',
  notas: '',
  dias: emptyDias(),
  equivalentes: emptyEquivalentes(),
};

const diasConComida = (dias) =>
  DIAS.filter(d => Object.values(dias?.[d] || {}).some(v => String(v ?? '').trim())).length;

const PlantillaCard = ({ plantilla, onEdit, onDelete }) => (
  <Card className="group hover:shadow-md transition-shadow">
    <CardContent className="p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{plantilla.nombre}</p>
          <p className="text-xs text-muted-foreground">
            {plantilla.modo === 'equivalentes'
              ? 'Equivalentes (SMAE)'
              : `Menú · ${diasConComida(plantilla.dias)} día${diasConComida(plantilla.dias) !== 1 ? 's' : ''} con comidas`}
          </p>
        </div>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(plantilla)} aria-label="Editar dieta">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => onDelete(plantilla)} aria-label="Eliminar dieta">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {plantilla.tipo_dieta && (
          <span className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
            {plantilla.tipo_dieta}
          </span>
        )}
        {plantilla.calorias_objetivo != null && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-500">
            <Flame className="h-3.5 w-3.5" />
            {plantilla.calorias_objetivo} kcal/día
          </span>
        )}
      </div>

      {plantilla.notas && (
        <p className="text-xs text-muted-foreground line-clamp-2">{plantilla.notas}</p>
      )}
    </CardContent>
  </Card>
);

export const DietasPlantilla = () => {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [activeDay, setActiveDay] = useState('lunes');
  const [bancoPorCategoria, setBancoPorCategoria] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchPlantillas = async () => {
    try {
      const res = await axios.get(`${API}/dietas-plantillas`);
      setPlantillas(res.data);
    } catch {
      toast.error('Error al cargar dietas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlantillas();
    axios.get(`${API}/alimentos`).then(res => {
      const agrupados = {};
      for (const a of res.data) {
        if (!agrupados[a.categoria]) agrupados[a.categoria] = [];
        agrupados[a.categoria].push(a);
      }
      setBancoPorCategoria(agrupados);
    }).catch(() => {});
  }, []);

  const abrirNueva = () => {
    setEditando(null);
    setForm(EMPTY_FORM);
    setActiveDay('lunes');
    setDialogOpen(true);
  };

  const abrirEditar = (p) => {
    setEditando(p);
    setForm({
      nombre: p.nombre,
      tipo_dieta: p.tipo_dieta ?? '',
      modo: p.modo || 'menu',
      calorias_objetivo: p.calorias_objetivo ?? '',
      proteinas_g: p.proteinas_g ?? '',
      carbohidratos_g: p.carbohidratos_g ?? '',
      grasas_g: p.grasas_g ?? '',
      notas: p.notas ?? '',
      dias: { ...emptyDias(), ...normPlanDias(p.dias) },
      equivalentes: { ...emptyEquivalentes(), ...(p.equivalentes || {}) },
    });
    setActiveDay('lunes');
    setDialogOpen(true);
  };

  const setDiaTiempo = (dia, tiempo, val) => {
    setForm(prev => ({
      ...prev,
      dias: { ...prev.dias, [dia]: { ...prev.dias[dia], [tiempo]: val } },
    }));
  };

  const copyDayToAll = () => {
    const source = form.dias[activeDay];
    setForm(prev => ({
      ...prev,
      dias: Object.fromEntries(DIAS.map(d => [d, { ...source }])),
    }));
    toast.success(`Comidas de ${DIA_LABEL[activeDay]} copiadas a todos los días`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    const payload = {
      nombre: form.nombre.trim(),
      tipo_dieta: form.tipo_dieta.trim() || null,
      modo: form.modo,
      calorias_objetivo: form.calorias_objetivo !== '' ? parseInt(form.calorias_objetivo) : null,
      proteinas_g:       form.proteinas_g       !== '' ? parseInt(form.proteinas_g)       : null,
      carbohidratos_g:   form.carbohidratos_g   !== '' ? parseInt(form.carbohidratos_g)   : null,
      grasas_g:          form.grasas_g          !== '' ? parseInt(form.grasas_g)          : null,
      notas: form.notas || null,
      dias: form.modo === 'menu' ? form.dias : {},
      equivalentes: form.modo === 'equivalentes'
        ? Object.fromEntries(
            Object.entries(form.equivalentes)
              .filter(([, v]) => v !== '' && v != null)
              .map(([k, v]) => [k, parseFloat(v)])
          )
        : {},
    };
    setSaving(true);
    try {
      if (editando) {
        await axios.put(`${API}/dietas-plantillas/${editando.id}`, payload);
        toast.success('Dieta actualizada');
      } else {
        await axios.post(`${API}/dietas-plantillas`, payload);
        toast.success('Dieta guardada — ya aparece al crear planes de pacientes');
      }
      setDialogOpen(false);
      fetchPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/dietas-plantillas/${deleteTarget.id}`);
      setPlantillas(prev => prev.filter(p => p.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success('Dieta eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div>
      <div className="flex justify-end mb-6">
        <Button onClick={abrirNueva} className="gap-2">
          <Plus className="h-4 w-4" />
          Nueva dieta
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i}><CardContent className="p-4 flex flex-col gap-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-5 w-24 rounded-full" />
            </CardContent></Card>
          ))}
        </div>
      ) : plantillas.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
            <NotebookText className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="font-semibold text-lg">No hay dietas guardadas aún</p>
          <p className="text-muted-foreground text-sm mt-1 max-w-sm">
            Crea dietas pre-elaboradas aquí, o guarda el plan de un paciente como dieta
            con el botón de marcador. Al crear un plan nuevo podrás partir de ellas.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plantillas.map(p => (
            <PlantillaCard key={p.id} plantilla={p} onEdit={abrirEditar} onDelete={setDeleteTarget} />
          ))}
        </div>
      )}

      {/* Dialog crear/editar dieta (mismo editor que los planes, sin fecha ni paciente) */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar dieta' : 'Nueva dieta pre-elaborada'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Nombre *</Label>
                <Input placeholder="Dieta 1200 kcal" value={form.nombre}
                  onChange={e => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Tipo de dieta</Label>
                <Input placeholder="Hipocalórica, diabético..." value={form.tipo_dieta}
                  onChange={e => setForm({ ...form, tipo_dieta: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { key: 'calorias_objetivo', label: 'kcal/día' },
                { key: 'proteinas_g',       label: 'Proteína g' },
                { key: 'carbohidratos_g',   label: 'Carbos g' },
                { key: 'grasas_g',          label: 'Grasas g' },
              ].map(f => (
                <div key={f.key} className="space-y-1">
                  <Label className="text-xs">{f.label}</Label>
                  <Input type="number" min="0" value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })} />
                </div>
              ))}
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Modalidad</Label>
              <div className="flex gap-2">
                {[
                  { v: 'menu',         label: 'Menú por días' },
                  { v: 'equivalentes', label: 'Equivalentes (SMAE)' },
                ].map(opt => (
                  <button key={opt.v} type="button" onClick={() => setForm({ ...form, modo: opt.v })}
                    className={`flex-1 py-2 rounded-lg border text-sm transition-colors cursor-pointer ${
                      form.modo === opt.v
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'border-border text-muted-foreground hover:border-emerald-400'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {form.modo === 'menu' && (
              <div>
                <div className="flex items-center gap-2">
                  <div className="flex overflow-x-auto gap-1 pb-1 flex-1">
                    {DIAS.map(d => (
                      <button key={d} type="button" onClick={() => setActiveDay(d)}
                        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
                          activeDay === d
                            ? 'bg-emerald-600 text-white'
                            : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                        }`}>
                        {DIA_LABEL[d]}
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={copyDayToAll}
                    className="shrink-0 px-3 py-1.5 rounded-lg text-xs bg-muted hover:bg-muted/80 text-muted-foreground transition-colors whitespace-nowrap cursor-pointer"
                    title={`Copiar comidas de ${DIA_LABEL[activeDay]} a todos los días`}>
                    Copiar a todos
                  </button>
                </div>
                <div className="mt-3 space-y-3">
                  {TIEMPOS.map(t => (
                    <AlimentoSelector
                      key={t.key}
                      tiempoKey={t.key}
                      tiempoLabel={t.label}
                      value={form.dias[activeDay]?.[t.key] ?? ''}
                      onChange={val => setDiaTiempo(activeDay, t.key, val)}
                      bancoPorCategoria={bancoPorCategoria}
                      diasForm={form.dias}
                      diaActivo={activeDay}
                    />
                  ))}
                </div>
              </div>
            )}

            {form.modo === 'equivalentes' && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Equivalentes / porciones por grupo de alimentos al día.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SMAE_GRUPOS.map(g => (
                    <div key={g.key} className="flex items-center gap-2">
                      <Label className="text-xs flex-1">{g.label}</Label>
                      <Input type="number" min="0" step="0.5" className="w-20"
                        value={form.equivalentes[g.key] ?? ''}
                        onChange={e => setForm(prev => ({ ...prev, equivalentes: { ...prev.equivalentes, [g.key]: e.target.value } }))} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1">
              <Label>Notas generales</Label>
              <Textarea rows={2} value={form.notas}
                onChange={e => setForm({ ...form, notas: e.target.value })} />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
              <Button type="submit" className="flex-1" disabled={saving}>
                {saving ? 'Guardando...' : editando ? 'Guardar cambios' : 'Crear dieta'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmar eliminar */}
      <Dialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar dieta</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            ¿Eliminar <strong>{deleteTarget?.nombre}</strong>? Los planes ya asignados a pacientes no se afectan.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
